// Maps a locally-stored Application onto the exact payload shape the Django
// serializers expect, resolving any foreign keys (owner CNIC -> Owner id,
// UAS serial/registration number -> UASRegistration id) via search first,
// and switching to multipart when a document file is attached.
import { apiRequest, apiRequestMultipart, apiSearch } from "./api";
import { Application } from "./types";
import { APPLICATION_TYPES, ApplicationTypeKey, Category, Status } from "./rules";

const ENTITY_TYPE_MAP: Record<string, string> = {
  "Citizen of Pakistan": "citizen",
  "Body corporate registered in Pakistan": "body_corporate",
  "Federal / provincial government body": "government",
  "Foreign national / foreign entity": "foreign",
};

const ROLE_MAP: Record<string, string> = {
  Importer: "importer",
  Exporter: "exporter",
  Both: "both",
};

export const TYPE_ENDPOINT: Record<ApplicationTypeKey, string> = {
  owner: "/owners/",
  uas: "/uas-registrations/",
  transfer: "/transfers/",
  manufacturing: "/manufacturing-certificates/",
  importExport: "/import-export/",
  sduc: "/sduc-requests/",
};

const BACKEND_STATUS_MAP: Record<string, Status> = {
  draft: "Draft",
  submitted: "Submitted",
  under_review: "Under Review",
  decided: "Decided",
};

const bool = (v: unknown) => v === true || v === "true";
const str = (v: unknown) => (v == null ? "" : String(v));

interface OwnerRemote {
  id: number;
  cnic: string;
}
interface UasRemote {
  id: number;
  serial_number: string;
  registration_number: string;
}
interface RemoteRecord {
  id: number;
  status: string;
  decision: "pending" | "approved" | "rejected";
  decision_notes: string;
  certificate: string | null;
}

async function findOwnerIdByCnic(cnic: string): Promise<number | null> {
  const results = await apiSearch<OwnerRemote>("/owners/", cnic);
  const match = results.find((o) => o.cnic === cnic) ?? results[0];
  return match?.id ?? null;
}

async function findUasIdBySerialOrRegNo(value: string): Promise<number | null> {
  const results = await apiSearch<UasRemote>("/uas-registrations/", value);
  const match = results.find((u) => u.serial_number === value || u.registration_number === value) ?? results[0];
  return match?.id ?? null;
}

function toFormData(payload: Record<string, unknown>, files: Record<string, File>): FormData {
  const fd = new FormData();
  Object.entries(payload).forEach(([k, v]) => {
    if (v === undefined || v === null) return;
    fd.append(k, typeof v === "boolean" ? String(v) : String(v));
  });
  Object.entries(files).forEach(([k, f]) => fd.append(k, f));
  return fd;
}

export interface SyncResult {
  ok: boolean;
  error?: string;
  remoteId?: number;
  certificateUrl?: string | null;
}

async function submit(
  endpoint: string,
  payload: Record<string, unknown>,
  files: Record<string, File> | undefined,
  errBox: { msg: string }
): Promise<RemoteRecord | null> {
  const onError = (status: number, body: unknown) => {
    errBox.msg = `HTTP ${status}: ${JSON.stringify(body)}`;
  };
  if (files && Object.keys(files).length > 0) {
    return apiRequestMultipart<RemoteRecord>(endpoint, "POST", toFormData(payload, files), onError);
  }
  return apiRequest<RemoteRecord>(endpoint, { method: "POST", body: JSON.stringify(payload) }, onError);
}

/** POSTs one Application to its backend endpoint, resolving FKs first and
 * attaching any document files. Returns ok:false with a human-readable
 * reason (never throws) so the UI can show *why* a sync didn't happen. */
export async function syncApplication(app: Application, files: Record<string, File> = {}): Promise<SyncResult> {
  const d = app.data;
  const errBox = { msg: "" };

  switch (app.type) {
    case "owner": {
      // Owner accounts are created via /auth/register/ on the
      // registration page itself (it creates the login + profile together),
      // so there is nothing further to sync here.
      return { ok: true };
    }

    case "uas": {
      const ownerCnic = str(d.ownerCnic);
      const ownerId = await findOwnerIdByCnic(ownerCnic);
      if (!ownerId) {
        return { ok: false, error: `No registered owner found with CNIC ${ownerCnic}. Register the owner first.` };
      }
      const [product, ...rest] = app.title.split(" ");
      const payload = {
        owner: ownerId,
        product_name: str(d.product) || product || app.title,
        model: str(d.model) || rest.join(" "),
        serial_number: str(d.serial),
        mtow_grams: app.weightGrams ?? 0,
        purpose: str(d.purpose),
        conformity_assessment: bool(d.conformity),
        security_clearance: bool(d.securityClearance),
        hale_type: bool(d.haleType),
        ir_thermal_drop_payload: bool(d.irThermalDrop),
        dg_permission: bool(d.dgPermission),
      };
      const res = await submit(TYPE_ENDPOINT.uas, payload, files, errBox);
      return res
        ? { ok: true, remoteId: res.id, certificateUrl: res.certificate }
        : { ok: false, error: errBox.msg || "UAS registration was rejected by the backend." };
    }

    case "transfer": {
      const [transferorId, transfereeId, uasId] = await Promise.all([
        findOwnerIdByCnic(str(d.fromCnic)),
        findOwnerIdByCnic(str(d.toCnic)),
        findUasIdBySerialOrRegNo(str(d.serial)),
      ]);
      if (!transferorId || !transfereeId) {
        return { ok: false, error: "Transferor or transferee CNIC was not found among registered owners." };
      }
      if (!uasId) {
        return { ok: false, error: `No registered aircraft found with serial ${str(d.serial)}.` };
      }
      const payload = {
        uas: uasId,
        transferor: transferorId,
        transferee: transfereeId,
        transfer_statement: str(d.statement),
        notary_attestation: bool(d.notary),
      };
      const res = await submit(TYPE_ENDPOINT.transfer, payload, files, errBox);
      return res
        ? { ok: true, remoteId: res.id, certificateUrl: res.certificate }
        : { ok: false, error: errBox.msg || "Transfer was rejected by the backend." };
    }

    case "manufacturing": {
      const applicantId = await findOwnerIdByCnic(str(d.cnic));
      if (!applicantId) {
        return { ok: false, error: `No registered owner found with CNIC ${str(d.cnic)}. Register the owner first.` };
      }
      const duties = str(d.duties).split(",").map((s) => s.trim());
      const payload = {
        applicant: applicantId,
        applicant_type: ENTITY_TYPE_MAP[str(d.applicantType)] ?? "citizen",
        duty_design: duties.includes("Design"),
        duty_manufacture: duties.includes("Manufacture"),
        duty_repair: duties.includes("Repair"),
        minimum_requirements_doc: bool(d.minimumReqsDoc),
      };
      const res = await submit(TYPE_ENDPOINT.manufacturing, payload, files, errBox);
      return res
        ? { ok: true, remoteId: res.id, certificateUrl: res.certificate }
        : { ok: false, error: errBox.msg || "Certificate application was rejected by the backend." };
    }

    case "importExport": {
      const applicantId = await findOwnerIdByCnic(str(d.cnic));
      if (!applicantId) {
        return { ok: false, error: `No registered owner found with CNIC ${str(d.cnic)}. Register the owner first.` };
      }
      const payload = {
        applicant: applicantId,
        role: ROLE_MAP[str(d.role)] ?? "",
        personal_single_cat_i: bool(d.personalSingleCatI),
        no_crime_history: bool(d.noCrimeHistory),
        working_capital_declared: bool(d.workingCapitalDeclared),
      };
      const res = await submit(TYPE_ENDPOINT.importExport, payload, files, errBox);
      return res
        ? { ok: true, remoteId: res.id, certificateUrl: res.certificate }
        : { ok: false, error: errBox.msg || "Registration was rejected by the backend." };
    }

    case "sduc": {
      const uasId = await findUasIdBySerialOrRegNo(str(d.regNo));
      if (!uasId) {
        return { ok: false, error: `No registered aircraft found with registration number ${str(d.regNo)}.` };
      }
      const payload = {
        uas: uasId,
        district: str(d.district),
        requesting_department: str(d.requestingDept),
        purpose: str(d.purpose),
        area: str(d.area),
        duration: str(d.duration),
        timing: str(d.timing),
        police_intimated: bool(d.policeIntimated),
      };
      const res = await submit(TYPE_ENDPOINT.sduc, payload, files, errBox);
      return res
        ? { ok: true, remoteId: res.id, certificateUrl: res.certificate }
        : { ok: false, error: errBox.msg || "SDUC request was rejected by the backend." };
    }

    default:
      return { ok: false, error: "Unknown application type." };
  }
}

/** Re-fetches a single application's current status/decision/certificate
 * from the backend by its remote id — used for the "Refresh status" action
 * once a committee member may have reviewed it. */
export async function fetchStatus(app: Application): Promise<Partial<Application> | null> {
  if (!app.remoteId) return null;
  const res = await apiRequest<RemoteRecord>(`${TYPE_ENDPOINT[app.type]}${app.remoteId}/`);
  if (!res) return null;
  return {
    status: BACKEND_STATUS_MAP[res.status] ?? app.status,
    decision: res.decision,
    decisionNotes: res.decision_notes,
    certificateUrl: res.certificate,
  };
}

// --- Committee-facing helpers -------------------------------------------

export interface RemoteListItem extends RemoteRecord {
  [key: string]: unknown;
}

/** Fetches every record of one application type for committee review,
 * newest first, up to 100 records (paginated review is a reasonable next
 * step once a district's queue regularly exceeds that). */
export async function fetchAllRemote(type: ApplicationTypeKey): Promise<RemoteListItem[]> {
  const res = await apiRequest<{ results?: RemoteListItem[] } | RemoteListItem[]>(
    `${TYPE_ENDPOINT[type]}?page_size=100&ordering=-created_at`
  );
  if (!res) return [];
  return Array.isArray(res) ? res : res.results ?? [];
}

export async function decideRemote(
  type: ApplicationTypeKey,
  id: number,
  decision: "approved" | "rejected",
  decisionNotes: string
): Promise<RemoteListItem | null> {
  return apiRequest<RemoteListItem>(`${TYPE_ENDPOINT[type]}${id}/decide/`, {
    method: "POST",
    body: JSON.stringify({ decision, decision_notes: decisionNotes }),
  });
}

export async function advanceRemote(type: ApplicationTypeKey, id: number): Promise<RemoteListItem | null> {
  return apiRequest<RemoteListItem>(`${TYPE_ENDPOINT[type]}${id}/advance/`, { method: "POST" });
}

const TYPE_LABEL: Record<ApplicationTypeKey, string> = Object.fromEntries(
  APPLICATION_TYPES.map((t) => [t.key, t.label])
) as Record<ApplicationTypeKey, string>;

/** Human label for a remote record, using whatever name-bearing fields that
 * type's serializer actually exposes. Transfer/manufacturing/import-export/
 * SDUC only expose FK ids (not nested owner/aircraft names), so those fall
 * back to a generic "<type> #<id>" label. */
function remoteTitle(type: ApplicationTypeKey, item: RemoteListItem): string {
  switch (type) {
    case "owner":
      return str(item.name) || `Owner #${item.id}`;
    case "uas":
      return [str(item.product_name), str(item.model)].filter(Boolean).join(" ") || `UAS #${item.id}`;
    default:
      return `${TYPE_LABEL[type]} #${item.id}`;
  }
}

/** Maps one raw backend record (from fetchAllRemote) onto the frontend's
 * Application shape so it can render through the existing FlightStrip
 * unchanged. Backend records don't carry the frontend's local
 * ComplianceFlag list — a rejected decision becomes a single flag carrying
 * the reviewer's notes; otherwise flags is empty. */
export function remoteToApplication(type: ApplicationTypeKey, item: RemoteListItem): Application {
  const decision = item.decision;
  return {
    id: `${type}-${item.id}`,
    type,
    title: remoteTitle(type, item),
    category: type === "uas" ? ((item.category as Category) || undefined) : undefined,
    status: BACKEND_STATUS_MAP[item.status] ?? "Draft",
    createdAt: item.created_at ? new Date(str(item.created_at)).getTime() : Date.now(),
    updatedAt: item.updated_at ? new Date(str(item.updated_at)).getTime() : Date.now(),
    data: {},
    flags:
      decision === "rejected" && item.decision_notes
        ? [{ level: "block", message: str(item.decision_notes), rule: "" }]
        : [],
    sync: "synced",
    remoteId: item.id,
    certificateUrl: item.certificate,
    decision,
    decisionNotes: item.decision_notes,
  };
}
